import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/Button";

/**
 * Nút Lưu có XÁC NHẬN ĐÃ LƯU.
 *
 * Hệ cũ hiện một banner xanh rồi tự tắt sau 5 giây. Bản mới lưu xong không nói
 * gì cả: nút trở lại như cũ, biểu mẫu đứng yên, và người dùng không biết vừa
 * lưu được hay bấm hụt — nên họ bấm thêm lần nữa cho chắc.
 *
 * Không dựng hệ thống toast cho một việc này: xác nhận nằm ĐÚNG chỗ người dùng
 * vừa nhìn (chính cái nút họ bấm) thì rõ hơn một thông báo bay ra ở góc màn
 * hình. Hai giây rồi trở lại bình thường.
 */
export function SaveButton({
  onClick,
  saving,
  savedAt,
  disabled,
  children,
  ...rest
}: {
  onClick: () => void;
  saving: boolean;
  /**
   * Dấu thời gian của lần lưu thành công gần nhất (Date.now()). Đổi giá trị là
   * kích hoạt lời xác nhận — dùng mốc thời gian chứ không phải cờ boolean để
   * hai lần lưu liên tiếp vẫn hiện lại được.
   */
  savedAt?: number | null;
  disabled?: boolean;
  children?: React.ReactNode;
} & Omit<React.ComponentProps<typeof Button>, "onClick" | "children">) {
  const { t } = useTranslation();
  const [showSaved, setShowSaved] = useState(false);
  const seen = useRef<number | null>(null);

  useEffect(() => {
    if (!savedAt || savedAt === seen.current) return;
    seen.current = savedAt;
    setShowSaved(true);
    const id = setTimeout(() => setShowSaved(false), 2000);
    return () => clearTimeout(id);
  }, [savedAt]);

  return (
    <Button onClick={onClick} disabled={disabled || saving} {...rest}>
      {saving ? (
        t("common.saving")
      ) : showSaved ? (
        <>
          <Check className="size-4" /> {t("common.savedShort")}
        </>
      ) : (
        (children ?? t("common.save"))
      )}
    </Button>
  );
}
